import {
    ApiContentBlock,
    EDITOR_BANNER_BLOCK_IDX,
    EDITOR_FLOATING_BUTTON_BLOCK_IDX,
    EDITOR_SHOPPABLE_VIDEO_BLOCK_IDX,
} from '@storefronts/common-types'
import { QueryParamOverrideInput } from '@storefronts/shopper-frontend'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { v4 as uuid } from 'uuid'
import { SectionWithHash } from './editorblocks/sectionWithHash'
import { deepEqual } from '@/lib/utils'
import { transformContentToComparable } from './utils/transformToComparable'
import { BANNER_SECTION_HASH } from './banner/constants'
import { SHOPPABLE_VIDEO_SECTION_HASH } from './shoppable-video/constants'
import { FLOATING_BUTTON_SECTION_HASH } from './floating-button/constants'

type StagedContentContext = {
    stagedContent: ApiContentBlock
    localStagedSections: SectionWithHash[]
    setLocalStagedSections: React.Dispatch<
        React.SetStateAction<SectionWithHash[]>
    >
    insertSectionAt: (index: number, section: SectionWithHash) => void
    clearSelectionIfInvalid: (sections: SectionWithHash[]) => void
    qpOverrides: QueryParamOverrideInput
    setQpOverrides: React.Dispatch<
        React.SetStateAction<QueryParamOverrideInput>
    >
    selectedBlockIdx: number | undefined
    selectedSectionHash: string | null
    setSelectedSectionHash: React.Dispatch<React.SetStateAction<string | null>>
    isStagedContentChanged: () => boolean
}

const defaultQPOverrides: QueryParamOverrideInput = {
    IS_DEVELOPMENT: false,
    IS_PREVIEW: false,
    DISCOUNT_CODE: undefined,
    HIDE_CART_OVERRIDE: false,
    VARIANT_ID: undefined,
    EVENT_ID: undefined,
    P_EVENT_ID: undefined,
    SHOP: undefined,
    SHOP_CART: false,
    SHARE_CART: false,
}

const defaultOverridesContext: StagedContentContext = {
    stagedContent: {} as ApiContentBlock,
    localStagedSections: [],
    setLocalStagedSections: () => {},
    insertSectionAt: () => {},
    clearSelectionIfInvalid: () => {},
    qpOverrides: defaultQPOverrides,
    setQpOverrides: () => {},
    selectedBlockIdx: undefined,
    selectedSectionHash: null,
    setSelectedSectionHash: () => {},
    isStagedContentChanged: () => false,
}

export const StagedContentContext = React.createContext<StagedContentContext>(
    defaultOverridesContext,
)

export const StagedContentContextProvider: React.FC<{
    initContent: ApiContentBlock
    children?: React.ReactNode
}> = ({ initContent, children }) => {
    const [stagedContent, setStagedContent] =
        useState<ApiContentBlock>(initContent)
    const [qpOverrides, setQpOverrides] =
        useState<QueryParamOverrideInput>(defaultQPOverrides)
    const [selectedSectionHash, setSelectedSectionHashRaw] = useState<
        string | null
    >(null)

    const initializeLocalStagedSections = (): SectionWithHash[] => {
        return stagedContent.tabs[0].sections.map((s) => ({
            ...s,
            hash: uuid(),
        }))
    }

    const [localStagedSections, setLocalStagedSectionsState] = useState<
        SectionWithHash[]
    >(initializeLocalStagedSections())

    const localStagedSectionsRef = useRef(localStagedSections)

    const setLocalStagedSections = useCallback(
        (value: React.SetStateAction<SectionWithHash[]>) => {
            setLocalStagedSectionsState((prev) => {
                const next = typeof value === 'function' ? value(prev) : value
                localStagedSectionsRef.current = next
                return next
            })
        },
        [],
    )

    const insertSectionAt = useCallback(
        (index: number, section: SectionWithHash) => {
            setLocalStagedSections((prev) => {
                const next = [...prev]
                next.splice(index, 0, section)
                return next
            })
        },
        [setLocalStagedSections],
    )

    const clearSelectionIfInvalid = useCallback(
        (sections: SectionWithHash[]) => {
            setSelectedSectionHashRaw((current) => {
                if (
                    !current ||
                    current === BANNER_SECTION_HASH ||
                    current === SHOPPABLE_VIDEO_SECTION_HASH ||
                    current === FLOATING_BUTTON_SECTION_HASH
                ) {
                    return current
                }
                return sections.some((s) => s.hash === current) ? current : null
            })
        },
        [],
    )

    const selectedBlockIdx = useMemo(() => {
        if (!selectedSectionHash) return undefined
        if (selectedSectionHash === BANNER_SECTION_HASH) {
            return EDITOR_BANNER_BLOCK_IDX
        }
        if (selectedSectionHash === SHOPPABLE_VIDEO_SECTION_HASH) {
            return EDITOR_SHOPPABLE_VIDEO_BLOCK_IDX
        }
        if (selectedSectionHash === FLOATING_BUTTON_SECTION_HASH) {
            return EDITOR_FLOATING_BUTTON_BLOCK_IDX
        }
        const idx = localStagedSections.findIndex(
            (s) => s.hash === selectedSectionHash,
        )
        return idx === -1 ? undefined : idx
    }, [selectedSectionHash, localStagedSections])

    const setSelectedSectionHash = useCallback(
        (value: React.SetStateAction<string | null>) => {
            setSelectedSectionHashRaw((prev) => {
                const next = typeof value === 'function' ? value(prev) : value
                if (
                    next &&
                    next !== BANNER_SECTION_HASH &&
                    next !== SHOPPABLE_VIDEO_SECTION_HASH &&
                    next !== FLOATING_BUTTON_SECTION_HASH &&
                    !localStagedSectionsRef.current.some((s) => s.hash === next)
                ) {
                    return null
                }
                return next
            })
        },
        [],
    )

    const isStagedContentChanged = (
        newStagedContent: ApiContentBlock = stagedContent,
    ) => {
        const stagedSections = newStagedContent.tabs[0].sections.map((s) => {
            const { hash, ...rest } = s as SectionWithHash
            return rest
        })
        const stagedWithoutHash = {
            tabs: [
                {
                    ...newStagedContent.tabs[0],
                    sections: stagedSections,
                },
            ],
        }

        return !deepEqual(
            transformContentToComparable(stagedWithoutHash),
            transformContentToComparable(initContent),
        )
    }

    const updateStagedContent = (
        newStagedContentSections: SectionWithHash[],
    ): void => {
        const newStagedContent: ApiContentBlock = {
            ...stagedContent,
            tabs: [
                {
                    ...stagedContent.tabs[0],
                    sections: newStagedContentSections.map((ls) => {
                        const { hash, ...rest } = ls
                        return rest
                    }),
                },
            ],
        }

        setStagedContent(newStagedContent)
    }

    // Updates Staged Content whenever local section edits happen
    useEffect(() => {
        // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler
        if (!localStagedSections || !stagedContent) {
            return
        }

        // eslint-disable-next-line react-you-might-not-need-an-effect/no-derived-state
        updateStagedContent(localStagedSections)
    }, [localStagedSections]) // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <StagedContentContext.Provider
            value={{
                stagedContent,
                localStagedSections,
                setLocalStagedSections,
                insertSectionAt,
                clearSelectionIfInvalid,
                qpOverrides,
                setQpOverrides,
                selectedBlockIdx,
                selectedSectionHash,
                setSelectedSectionHash,
                isStagedContentChanged,
            }}
        >
            {children}
        </StagedContentContext.Provider>
    )
}
